import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Header from '../../components/layout/Header';
import {
  LayoutDashboard,
  Globe,
  Search,
  Palette,
  Euro,
  FileText,
  Image,
  Users,
  Megaphone,
  Settings,
  MessageCircle,
  Clock,
  Shield,
  ArrowLeft,
  Code,
  Monitor,
  Briefcase,
  Layers
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';

const AdminLayout = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  // Redirect non-agency users
  if (!isAgency) {
    navigate('/dashboard/agency');
    return null;
  }

  const adminNavigation = [
    { 
      category: 'CMS - Contenu',
      items: [
        { to: '/admin/cms', icon: Layers, label: '📦 CMS Complet' },
      ]
    },
    { 
      category: 'Site Vitrine',
      items: [
        { to: '/admin/home', icon: Globe, label: 'Page d\'accueil' },
        { to: '/admin/pages', icon: FileText, label: 'Autres pages' },
        { to: '/admin/portfolio', icon: Briefcase, label: 'Portfolio' },
        { to: '/admin/team', icon: Users, label: 'Équipe' },
        { to: '/admin/gallery', icon: Image, label: 'Galerie clients' },
      ]
    },
    {
      category: 'Marketing',
      items: [
        { to: '/admin/seo', icon: Search, label: 'SEO & Pages villes' },
        { to: '/admin/news', icon: Megaphone, label: 'Actualités' },
        { to: '/admin/reviews', icon: MessageCircle, label: 'Avis Google' },
        { to: '/admin/newsletter', icon: Users, label: 'Liste d\'attente' },
      ]
    },
    {
      category: 'Configuration',
      items: [
        { to: '/admin/pricing', icon: Euro, label: 'Tarification & Packs' },
        { to: '/admin/theme', icon: Palette, label: 'Thème & Couleurs' },
        { to: '/admin/header', icon: Monitor, label: 'Bandeau & Horaires' },
        { to: '/admin/legal', icon: FileText, label: 'Pages légales' },
        { to: '/admin/settings', icon: Settings, label: 'Paramètres' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Admin Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full z-40 transition-all duration-300 hidden md:flex flex-col w-64",
          "bg-gradient-to-b from-amber-900 to-amber-950"
        )}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">ADMIN</h1>
              <p className="text-white/50 text-xs">Gestion du site</p>
            </div>
          </div>
        </div>

        {/* Back to V1 */}
        <div className="p-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard/agency')}
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'Espace Client
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {adminNavigation.map((group, idx) => (
            <div key={idx} className="mb-6">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-wider px-3 mb-2">
                {group.category}
              </p>
              <div className="space-y-1">
                {group.items.map(item => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm",
                          isActive
                            ? "bg-amber-500/20 text-amber-300 font-medium"
                            : "text-white/60 hover:text-white hover:bg-white/5"
                        )
                      }
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Mode Switcher */}
        <div className="p-4 border-t border-white/10">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => navigate('/dashboard/agency')}
              className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs text-white/50 hover:text-white/70 hover:bg-white/5 transition-all"
            >
              <Users className="w-4 h-4" />
              <span>V1</span>
            </button>
            <button
              onClick={() => window.open('/develop', '_blank')}
              className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs text-white/50 hover:text-white/70 hover:bg-white/5 transition-all"
            >
              <Code className="w-4 h-4" />
              <span>DEV</span>
            </button>
            <button
              className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs bg-amber-500/20 text-amber-300 border border-amber-400/30"
            >
              <Shield className="w-4 h-4" />
              <span>ADMIN</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="md:ml-64">
        <Header collapsed={false} setMobileOpen={() => {}} />
        <main className="p-6 md:p-8 pt-28 md:pt-32">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
